import * as THREE from 'three';
import { DxfWriter } from '@tarikjabiri/dxf';
import entityHandlers from './entityHandlers';
import { getClosestColorIndex } from '../../utils/colorUtils';
import { generateOLE2FrameDXF, createOLE2FrameBlob, calculateBounds } from './OLE2FrameService';
import { isExportHidden } from '../../utils/switchHiddenState';

/**
 * DXFWriter - A class to handle the generation of DXF content from a Three.js scene.
 * It configures the DXF document, processes entities from the scene using a set of handlers,
 * and serializes the final DXF content to a string.
 */
class DXFWriter {
  /**
   * Generates a DXF string from a given Three.js scene and application state.
   *
   * This method initializes a DXF document, sets up necessary styles and layers,
   * traverses the scene to find exportable entities, and uses the `entityHandlers`
   * to convert Three.js objects into DXF entities. It also handles color and layer assignments.
   *
   * @param {THREE.Scene} scene - The Three.js scene containing the objects to export.
   * @param {object} state - The application state, containing layer definitions and other settings.
   * @returns {string} The generated DXF content as a string.
   */
  generateDXF(scene, state) {
    const writer = new DxfWriter();
    writer.setVariable('$PROJECTNAME', { 1: 'Generated by ProjeHUB-E4' });
    writer.setVariable('$DWGCODEPAGE', { 3: 'ANSI_1254' });

    const arialStyle = writer.document.tables.styleTable.addStyle('Arial');
    arialStyle.fontFileName = 'Arial.ttf';

    state.layer.allLayers
      .filter(layer => layer.name !== 'Defpoints')
      .forEach(layer => {
        writer.addLayer(layer.name, getClosestColorIndex(layer.color));
      });

    // Composite entity types that should be silently skipped (their children will be exported instead)
    const compositeTypes = new Set([
      'block',
      'supplyPoint',
      'mainPanel',
      'electricalPanel',
      'panelCon_out',
      'machine',
      'permanent',
      'BellTrafo',
      'bellTrafo',
      'trafoOutput',
      'bell',
      'Bell',
      'wireTitle',
      'Group',  // Three.js Group nesneleri - nested gruplar için
      'group',  // Küçük harfli variant
      'socket', // Socket grupları
      'panel',   // Panel grupları
      'lightSwitch',  // Anahtar grupları
      'fixture',  // Armatür grupları
      'groundingStrake'  // Topraklama kazığı grupları - çocukları ayrı export edilir
    ]);

    // Collect OLE2FRAME entities (embedded images) separately
    const ole2frameEntities = [];

    if (scene) {
      let exportedCount = 0;
      let skippedNoType = 0;
      let skippedNoHandler = 0;
      let skippedComposite = 0;

      scene.traverse(child => {
        // Skip objects without userData
        if (!child.userData) {
          child.userData = {};
        }


        // Skip Defpoints layer
        if (child.userData.layerName === 'Defpoints') {
          return;
        }

        // Skip children of pattern hatch groups (they are visual only, the hatch itself is exported)
        if (child.parent?.userData?.isPatternHatch) {
          return;
        }

        // Text grubu glyph mesh'ini atla: text içeriği parent grup üzerinden 'text'
        // entity olarak export edilir; texture'lı render plane'i geometri olarak
        // export EDİLMEZ. (Load sonrası bu plane PlaneGeometry'den generic
        // BufferGeometry'ye dönüşünce meshfill yanlışlıkla solid dörtgen üretiyordu —
        // fixture etiketlerinin yanındaki istenmeyen hatch'ler.)
        if (child.parent?.userData?.type === 'text') {
          return;
        }

        // ✅ Skip transparent click areas (invisible hatch surfaces used for easier selection)
        // These are identified by: low opacity, or name containing 'click-hatch' or 'box-hatch'
        const isClickArea =
          child.userData?.originalOpacity === 0.01 ||
          (child.material?.opacity !== undefined && child.material.opacity < 0.1) ||
          (child.name && (child.name.includes('click-hatch') || child.name === 'box-hatch'));

        if (isClickArea) {
          return;
        }

        // ✅ Gizlenen anahtar (veya exportHidden atası) — PDF/DXF'e export edilmez
        if (isExportHidden(child)) {
          return;
        }

        // Skip Three.js container objects (their children will be processed recursively by traverse)
        const containerTypes = ['Group', 'Scene', 'Object3D'];
        if (containerTypes.includes(child.type) && !child.userData.type) {
          return;
        }

        // Determine entity type
        let entityType = child.userData.type;

        // ✅ Wire type mapping - tüm wire tipleri 'line' olarak export edilir
        const wireTypes = [
          'wireLighting',
          'wireSocket',
          'wireMachine',
          'wireBell',
          'wireLightingFixture',
          'curvedLine',
          'groundingStrip'  // Topraklama şeridi çizgisi
        ];
        if (wireTypes.includes(entityType)) {
          entityType = 'line';
        }

        // ✅ Grounding label mapping - topraklama etiketleri text olarak export edilir
        const groundingLabelTypes = [
          'groundingLabel',      // "Galvaniz topraklama şeridi" yazısı
          'groundingBuatLabel'   // Bağlantı filizi yazısı
        ];
        if (groundingLabelTypes.includes(entityType)) {
          entityType = 'text';
        }

        // ✅ Buat type mapping - tüm buat tipleri için type'ı temizle ki geometri
        // tipine göre otomatik tanıma devreye girsin. Buat'lar dolu mesh'tir:
        // CircleGeometry/PlaneGeometry → meshfill (solid HATCH dolgu).
        const buatTypes = [
          'buatCircle',
          'lightingBuat',
          'buatLightingCircle',
          'buatLightingSquare',
          'socketBuat',
          'buatSquare'
        ];
        if (buatTypes.includes(entityType)) {
          // Buat'lar her zaman dolu mesh (CircleGeometry/PlaneGeometry) → doğrudan
          // meshfill (solid HATCH). Type'ı null'a çekip geometri-inference'a bırakmıyoruz;
          // çünkü PlaneGeometry inference yazı (text) düzlemlerini de yanlışlıkla
          // yakalıyordu. Buat'ı tipinden tanıyıp net yönlendiriyoruz.
          entityType = 'meshfill';
        }

        // ✅ Fallback: Infer type from Three.js object if not explicitly set
        if (!entityType) {
          if (child.type === 'Line' || child.type === 'LineSegments' || child.type === 'LineLoop') {
            entityType = 'line';
            
          } else if (child.type === 'Mesh' && child.geometry) {
            // Check geometry type for mesh
            // ✅ Dolu disk (CircleGeometry Mesh) = içi dolu sembol/buat/nokta → meshfill
            // (gerçek solid HATCH dolgu). Outline daireler LineLoop'tur ve buraya düşmez,
            // dolayısıyla CircleGeometry Mesh her zaman dolgu demektir — 'circle' (sadece
            // dış çizgi) export'u dolguyu kaybettiriyordu.
            if (child.geometry.type === 'CircleGeometry') {
              entityType = 'meshfill';
            } else if (child.geometry.type === 'ShapeGeometry') {
              // ✅ İçi dolu sembol taraması (priz/anahtar/makine/fixture solid hatch).
              // ShapeGeometry Mesh = dolu şekil → meshfill (her üçgen solid HATCH).
              // NOT: PlaneGeometry'yi BURAYA EKLEME — yazı (text) düzlemleri de
              // PlaneGeometry'dir ve solid'e dönüşüp çizimi kirletir. Kare buat'lar
              // tipinden (buatTypes) zaten meshfill'e yönlendiriliyor.
              entityType = 'meshfill';
            } else if (child.geometry.type === 'BufferGeometry') {
              // Mesh + BufferGeometry = içi dolu üçgenlenmiş yüzey. Save/load sonrası
              // CircleGeometry/ShapeGeometry gibi THREE alt-sınıfları generic
              // BufferGeometry'ye dönüşüyor (örn. makinelerin dolu noktası deserialize
              // edilince). Mesh her zaman dolu yüzeydir → meshfill (solid HATCH).
              // Outline'lar LineLoop/Line'dır ve bu 'Mesh' dalına düşmez.
              // ⚠️ texture'lı (material.map) mesh'ler text/görsel render plane'idir →
              // solid YAPMA (solid dolgular düz renktir, map taşımaz).
              const posAttr = child.geometry.attributes.position;
              if (child.material?.map) {
                // text/görsel render plane'i — geometri olarak export edilmez
              } else if (posAttr && posAttr.count >= 3) {
                entityType = 'meshfill';
              } else if (posAttr && posAttr.count >= 2) {
                entityType = 'polyline';
              }
            }
          } else if (child.type === 'Sprite') {
            // Check if this is a text sprite
            if (child.userData.text || child.name?.includes('text') || child.name?.includes('Text')) {
              entityType = 'text';
              
            }
          }
        }

        // Skip if still no type
        if (!entityType) {
          if (child.geometry) {
            console.warn('⚠️ DXF Export: Cannot infer type for object', {
              name: child.name,
              threeType: child.type,
              geometryType: child.geometry?.type,
              userData: child.userData
            });
            skippedNoType++;
          }
          return;
        }

        // ✅ Dolu solid hatch mesh'leri (panel/mainPanel/sembol) geometriden meshfill ile
        // doldur. İki neden:
        //  (a) Save/load sonrası bu mesh'ler type='hatch' alıyor ama boundaryPaths taşımıyor.
        //  (b) Fresh (yeni çizilen) panel/mainPanel boundaryPaths taşıyor AMA bu boundaryPaths
        //      düz edge dizisi (groups: [edge1, edge2]); 'hatch' handler her edge'i ayrı,
        //      KAPANMAMIŞ boundary olarak yazıyor → dolgu çıkmıyordu.
        // meshfill her iki durumda da ekrandaki gerçek üçgenlenmiş geometriyi birebir yazar
        // (konum/açı/ölçek dahil), bu yüzden boundaryPaths'tan bağımsız olarak en güvenilir yol.
        // Pattern hatch'leri Group'tur (Mesh değil) → bu koşula düşmez, etkilenmez.
        if (entityType === 'hatch' && child.type === 'Mesh' && child.geometry) {
          // Dolu solid hatch mesh'i (panel/mainPanel/sembol). Geometri tipi ne olursa
          // olsun (ShapeGeometry, CircleGeometry, ya da save/load sonrası generic
          // BufferGeometry'ye dönüşmüş) meshfill ile ekrandaki gerçek üçgenlenmiş yüzeyi
          // solid HATCH olarak yaz. Pattern hatch'leri Group'tur (Mesh değil) → etkilenmez.
          entityType = 'meshfill';
        }

        // Handle embedded images (OLE2FRAME) separately
        if (entityType === 'embeddedimage' || entityType === 'ole2frame' ||
            entityType === 'embeddedImage' || entityType === 'OLE2FRAME') {
          // ── Passthrough modu: DXF'ten import edilen OLE2FRAME ────────────
          // userData.binaryHex varsa orijinal CFB blob'unu olduğu gibi yeniden
          // yaz. Konum/ölçek için mesh'in mevcut world pozisyonunu kullan
          // (kullanıcı taşımış/ölçeklemiş olabilir).
          const binaryHex = child.userData.binaryHex;
          if (binaryHex && typeof binaryHex === 'string' && binaryHex.length >= 2) {
            const worldPos = new THREE.Vector3();
            child.getWorldPosition(worldPos);
            const worldScale = new THREE.Vector3();
            child.getWorldScale(worldScale);

            const w = (child.userData.oleWidth  || 1) * Math.abs(worldScale.x || 1);
            const h = (child.userData.oleHeight || 1) * Math.abs(worldScale.y || 1);

            // Mesh merkez konumlu — DXF OLE2FRAME upperLeft/lowerRight köşelerini bekler
            const z = worldPos.z || 0;
            const x0 = worldPos.x - w / 2, x1 = worldPos.x + w / 2;
            const y0 = worldPos.y - h / 2, y1 = worldPos.y + h / 2;
            const bounds = {
              upperLeft:  { x: x0, y: y1, z },
              lowerRight: { x: x1, y: y0, z },
            };

            // ⚠️ App-üretimi OLE (Yükleme Cetveli / Topraklama Hesabı): iç 128-byte
            // position header template'in ESKİ koordinatlarını taşıyor. Bu header
            // AutoCAD'in OLE içeriğini (presentation) nereye çizeceğini belirliyor;
            // eski koordinat kalırsa içerik yanlış konuma (orijine) çizilip frame boş
            // görünüyor. Header'ın 4 köşesini canlı bounds'a yeniden yaz + 254 char
            // yeniden chunk'la. İMPORT edilmiş OLE'lerde (bayrak yok) ELLEME — orijinal
            // blob/chunk yapısı aynen korunur (önceki kazanım).
            let hexLines = null;
            if (child.userData.oleAppGenerated && binaryHex.toUpperCase().startsWith('8055')) {
              let fixedHex = binaryHex.toUpperCase();
              const setD = (hex, byteOff, val) => {
                const bb = new Uint8Array(8);
                new DataView(bb.buffer).setFloat64(0, val, true);
                let s = '';
                for (const b of bb) s += b.toString(16).padStart(2, '0').toUpperCase();
                return hex.slice(0, byteOff * 2) + s + hex.slice(byteOff * 2 + 16);
              };
              // upperLeft@2, upperRight@26, lowerRight@50, lowerLeft@74 (her Vec3: x,y,z)
              const corners = [[2, x0, y1], [26, x1, y1], [50, x1, y0], [74, x0, y0]];
              for (const [off, cx, cy] of corners) {
                fixedHex = setD(fixedHex, off, cx);
                fixedHex = setD(fixedHex, off + 8, cy);
                fixedHex = setD(fixedHex, off + 16, z);
              }
              hexLines = [];
              const CHUNK = 254;
              for (let i = 0; i < fixedHex.length; i += CHUNK) hexLines.push(fixedHex.slice(i, i + CHUNK));
            }

            // İmport OLE: orijinal chunk yapısını koru; yoksa 254 char böl.
            if (!hexLines) {
              hexLines = child.userData.hexChunks;
              if (!Array.isArray(hexLines) || hexLines.length === 0) {
                hexLines = [];
                const CHUNK = 254;
                const HEX = binaryHex.toUpperCase();
                for (let i = 0; i < HEX.length; i += CHUNK) hexLines.push(HEX.slice(i, i + CHUNK));
              }
            }

            ole2frameEntities.push({
              passthrough: true,
              hexLines,
              bounds,
              layerName:   child.userData.layerName  || '0',
              itemName:    child.userData.itemName   || 'OLE',
              linkName:    child.userData.linkName   || 'OLE',
              oleVersion:  child.userData.oleVersion ?? 2,
              oleType:     child.userData.oleType    ?? 2,
              plotQuality: child.userData.plotQuality ?? 0,
            });
            exportedCount++;
            return;
          }

          // ── Image rebuild modu: createEmbeddedImage ile yeni eklenen resim ──
          const imageData = child.userData.imageData;
          if (imageData && imageData.data) {
            const worldPos = new THREE.Vector3();
            child.getWorldPosition(worldPos);

            const width = child.userData.width || 100;
            const height = child.userData.height || 100;
            const scale = child.userData.scale || 1;

            const bounds = calculateBounds(
              { x: worldPos.x, y: worldPos.y, z: worldPos.z || 0 },
              width * scale,
              height * scale
            );

            const oleBlob = createOLE2FrameBlob(imageData, bounds);

            ole2frameEntities.push({
              hexLines: oleBlob.hexLines,
              bounds,
              layerName: child.userData.layerName || '0'
            });

            exportedCount++;
          }
          return;
        }

        const handler = entityHandlers[entityType.toLowerCase()];
        if (handler) {
          const exportUserData = {
            ...child.userData,
            type: entityType,  // Ensure type is set
            threeObject: child
          };

          // Set color if not already defined
          if (exportUserData.colorNumber === undefined) {
            const colorSource = exportUserData.originalColor ||
                                (child.material ? child.material.color : null) ||
                                exportUserData.color;

            if (colorSource) {
              exportUserData.colorNumber = getClosestColorIndex(colorSource);
            }
          }

          handler(writer, exportUserData);
          exportedCount++;
        } else {
          // Check if this is a composite type that should be silently skipped
          if (compositeTypes.has(entityType)) {
            skippedComposite++;
            // Silently skip - children will be exported
          } else {
            console.warn(`⚠️ DXF Export: Unsupported entity type: ${entityType}`);
            skippedNoHandler++;
          }
        }
      });
    }

    let dxfContent = writer.stringify();

    // Inject OLE2FRAME entities if any
    if (ole2frameEntities.length > 0) {
      dxfContent = this.injectOLE2FrameEntities(dxfContent, ole2frameEntities);
    }

    // Inject pattern definitions for patterns that have imported patternLine data
    dxfContent = this.injectPatternDefinitions(dxfContent, writer._arPatternData);

    return dxfContent;
  }

  /**
   * Generates DXF pattern definition lines from imported pattern data
   * @param {Array} patternLines - Array of pattern line objects from import
   * @returns {string} - DXF group codes for pattern definition
   */
  generatePatternDxfLines(patternLines) {
    if (!patternLines || patternLines.length === 0) return null;

    let dxf = '';
    // 78 = Number of pattern definition lines
    dxf += `78\n${patternLines.length}\n`;

    patternLines.forEach(line => {
      // 53 = Pattern line angle
      dxf += `53\n${line.lineAngle || 0}\n`;
      // 43 = Pattern line base point X
      dxf += `43\n${line.LineBasePointX || 0}\n`;
      // 44 = Pattern line base point Y
      dxf += `44\n${line.LineBasePointY || 0}\n`;
      // 45 = Pattern line offset X
      dxf += `45\n${line.LineOffsetX || 0}\n`;
      // 46 = Pattern line offset Y
      dxf += `46\n${line.LineOffsetY || 0}\n`;
      // 79 = Number of dash length items
      const dashes = line.dashes || [];
      dxf += `79\n${dashes.length}\n`;
      // 49 = Dash length (repeated for each dash)
      dashes.forEach(dash => {
        dxf += `49\n${dash}\n`;
      });
    });

    return dxf;
  }

  /**
   * Injects pattern line definitions into HATCH entities
   * Uses imported pattern data from the scene, not hardcoded definitions
   * @param {string} dxfContent - Original DXF content
   * @param {Map} patternDataMap - Map of pattern name -> patternLine array
   * @returns {string} Modified DXF content with pattern definitions
   */
  injectPatternDefinitions(dxfContent, patternDataMap) {
    if (!patternDataMap || patternDataMap.size === 0) {
      return dxfContent;
    }

 
    // Split content by HATCH entities
    const parts = dxfContent.split(/(\n0\nHATCH\n)/);

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      // Skip the separator parts
      if (part === '\n0\nHATCH\n') continue;

      // Check each pattern in the map
      for (const [patternName, patternLines] of patternDataMap) {
        if (part.includes(`\n2\n${patternName}\n`)) {
          const patternDef = this.generatePatternDxfLines(patternLines);
          if (!patternDef) continue;

          // Match from 77 to 47, capturing any existing pattern definitions in between
          // This handles both:
          // - Custom patterns: ...77\n0\n47\n... (no 78 group code)
          // - Predefined patterns (ANSI, etc): ...77\n0\n78\n4\n53\n...(all pattern lines)...47\n...
          // We need to REPLACE existing pattern definitions with imported ones
          // Use non-greedy match to capture everything between 77 and 47
          const regexPatternBlock = /(\n77\n\d+\n)([\s\S]*?)(47\n)/;
          const matchBlock = part.match(regexPatternBlock);

          if (matchBlock) {
             // Replace any existing pattern definitions with imported ones
            const replacement = matchBlock[1] + patternDef + matchBlock[3];
            parts[i] = part.replace(regexPatternBlock, replacement);
          }
        }
      }
    }

    return parts.join('');
  }

  /**
   * Injects OLE2FRAME entities into the DXF content
   * OLE2FRAME entities must be added to the ENTITIES section
   * @param {string} dxfContent - Original DXF content
   * @param {Array} ole2frameEntities - Array of OLE2FRAME entity data
   * @returns {string} Modified DXF content
   */
  injectOLE2FrameEntities(dxfContent, ole2frameEntities) {
    // ⚠️ Handle'lar $HANDSEED'den TÜRETİLMELİ. DXF kuralı: $HANDSEED dosyadaki tüm
    // handle'lardan büyük olmalı. Eski kod sabit 0xFF00 kullanıyordu; çizim büyükse
    // gerçek handle'lar 0xFF00'i geçince çakışıyor, küçükse OLE handle'ı $HANDSEED'i
    // aşıyordu → AutoCAD dosyayı geçersiz sayıp OLE entity'lerini render etmeyebiliyor
    // (tutarsız "bazen dolu bazen boş" davranışının kaynağı).
    const seedMatch = dxfContent.match(/(\n\s*9\n\$HANDSEED\n\s*5\n)([0-9A-Fa-f]+)/);
    let baseHandle = 0xFF00;
    if (seedMatch) {
      const seed = parseInt(seedMatch[2], 16);
      if (!isNaN(seed)) baseHandle = seed;
    }

    // Generate OLE2FRAME DXF strings (each entity separated by group code 0)
    const ole2frameStrings = ole2frameEntities.map((entity, index) => {
      const handle = (baseHandle + index).toString(16).toUpperCase();

      // Passthrough modu — DXF'ten import edilen OLE2FRAME için orijinal alanları
      // koruyarak doğrudan grup kodu üret. AutoCAD class adı (group 3) ve OLE
      // type (group 71) doğru olmazsa "Press ENTER to continue" uyarısı verir.
      if (entity.passthrough) {
        const b = entity.bounds;
        const binarySize = entity.hexLines.reduce((s, l) => s + l.length / 2, 0);
        const lines = [
          '0', 'OLE2FRAME',
          '5', handle,
          '100', 'AcDbEntity',
          '8', entity.layerName || '0',
          '100', 'AcDbOle2Frame',
          '70', String(entity.oleVersion ?? 2),
          '3', entity.itemName || 'OLE',
          '10', b.upperLeft.x.toFixed(6),
          '20', b.upperLeft.y.toFixed(6),
          '30', (b.upperLeft.z || 0).toFixed(6),
          '11', b.lowerRight.x.toFixed(6),
          '21', b.lowerRight.y.toFixed(6),
          '31', (b.lowerRight.z || 0).toFixed(6),
          '71', String(entity.oleType ?? 2),
          '72', String(entity.plotQuality ?? 0),
          '90', String(binarySize),
        ];
        for (const hexLine of entity.hexLines) {
          lines.push('310', hexLine);
        }
        lines.push('1', entity.linkName || 'OLE');
        return lines.join('\n');
      }

      return generateOLE2FrameDXF({
        hexLines: entity.hexLines,
        bounds: entity.bounds,
        layerName: entity.layerName,
        handle
      });
    }).join('\n');

    // $HANDSEED'i OLE handle'larının üstüne çek (next free handle).
    let content = dxfContent;
    if (seedMatch) {
      const newSeed = (baseHandle + ole2frameEntities.length).toString(16).toUpperCase();
      content = content.replace(/(\n\s*9\n\$HANDSEED\n\s*5\n)[0-9A-Fa-f]+/, `$1${newSeed}`);
    }

    // ENTITIES section'ının sonundaki ENDSEC'i bul (header/classes/tables/blocks
    // section'larının ENDSEC'lerini değil!). Aksi halde OLE bloku yanlış
    // section'a enjekte oluyor ve AutoCAD format hatası uyarısı veriyor.
    const entitiesMarker = content.search(/\n {0,2}2\nENTITIES\n/);
    if (entitiesMarker !== -1) {
      const after = content.slice(entitiesMarker);
      const endSecRel = after.search(/\n {0,2}0\nENDSEC\n/);
      if (endSecRel !== -1) {
        const insertIndex = entitiesMarker + endSecRel;
        return content.slice(0, insertIndex) +
               '\n' + ole2frameStrings +
               content.slice(insertIndex);
      }
    }

    console.warn('Could not find ENTITIES section ENDSEC for OLE2FRAME injection');
    return content;
  }


}

export { DXFWriter };